import React, { useEffect, useMemo, useState } from 'react';
import { FiX, FiClock, FiRefreshCw } from 'react-icons/fi';
import { useLanguage } from '../../context/LanguageContext';
import './Modals.css';

const MAX_MINUTES = 26;

const FALLBACK_PHASES = [
  { name: 'Anamnesis', minutes: 6 },
  { name: 'Physical Exam', minutes: 5 },
  { name: 'Differential & Tests', minutes: 5 },
  { name: 'Evaluation & Management', minutes: 10 }
];

const localizedCaseText = {
  case_002: {
    ru: { name: 'Пациентка', chiefComplaint: 'Симптомы, характерные для гипертиреоза', gender: 'женский' },
    uz: { name: 'Ayol bemor', chiefComplaint: 'Gipertireozga xos belgilar', gender: 'ayol' }
  },
  case_003: {
    ru: { name: 'Пациент шри-ланкийского происхождения', chiefComplaint: 'Предоперационная подготовка к трансплантации печени, стойкая гипергликемия и трудный контроль давления', ethnicity: 'шри-ланкиец' },
    uz: { name: 'Shri-Lanka millatiga mansub bemor', chiefComplaint: 'Jigar transplantatsiyasi oldidan tayyorgarlik, turg‘un giperglikemiya va bosimni nazorat qilish qiyinligi', ethnicity: 'shri-lankalik' }
  },
  case_004: {
    ru: { name: 'Молодая пациентка', chiefComplaint: 'Утомляемость, головокружение, снижение массы тела и повторные обмороки', gender: 'женский', ethnicity: 'не указана' },
    uz: { name: 'Yosh ayol bemor', chiefComplaint: 'Charchoq, bosh aylanishi, vazn yo‘qotish va takroriy hushdan ketish', gender: 'ayol', ethnicity: 'ko‘rsatilmagan' }
  },
  case_005: {
    ru: { name: 'Бездомная пациентка южноазиатского происхождения', chiefComplaint: 'Постоянная утомляемость, полиурия, полидипсия и недавнее снижение массы тела', gender: 'женский', ethnicity: 'южноазиатское происхождение' },
    uz: { name: 'Janubiy osiyolik, boshpanasiz ayol bemor', chiefComplaint: 'Doimiy charchoq, poliuriya, polidipsiya va yaqinda vazn yo‘qotish', gender: 'ayol', ethnicity: 'janubiy osiyolik' }
  },
  case_006: {
    ru: { name: 'Пациент египетского происхождения', chiefComplaint: 'Снижение расчётной СКФ по последним анализам крови, утомляемость и отёки лодыжек', gender: 'мужской', ethnicity: 'египтянин' },
    uz: { name: 'Misrlik bemor', chiefComplaint: 'So‘nggi qon tahlillarida hisoblangan KFT pasayishi, charchoq va to‘piqda shish', gender: 'erkak', ethnicity: 'misrlik' }
  },
  case_007: {
    en: { name: 'Jasur Omonov', chiefComplaint: 'Frequent urination, palpitations, weakness, nausea, vomiting, shortness of breath, and weight loss', gender: 'male', ethnicity: 'Uzbek' },
    ru: { name: 'Жасур Омонов', chiefComplaint: 'Частое мочеиспускание, сердцебиение, слабость, тошнота, рвота, одышка и снижение массы тела', gender: 'мужской', ethnicity: 'узбек' },
    uz: { name: 'Jasur Omonov', chiefComplaint: 'Tez-tez siyish, yurak urib ketishi, holsizlik, ko‘ngil aynishi, qusish, nafas qisishi va vazn yo‘qotish', gender: 'erkak', ethnicity: 'o‘zbek' }
  }
};

const minutesFromPhase = (phase) => {
  if (phase.minutes) return Number(phase.minutes);
  return Math.max(1, Math.round((phase.duration || 0) / 60000));
};

const TimerModal = ({
  isOpen,
  onClose,
  onStartSession,
  patient,
  patientImage,
  phases = FALLBACK_PHASES,
  defaultPhases = FALLBACK_PHASES
}) => {
  const { language, t, phaseName } = useLanguage();
  const [draft, setDraft] = useState(phases);
  const [imageFailed, setImageFailed] = useState(false);
  const localizedPatient = patient
    ? { ...patient, ...(localizedCaseText[patient.id]?.[language] || {}) }
    : null;

  useEffect(() => {
    if (isOpen) {
      setDraft((phases.length ? phases : FALLBACK_PHASES).map((phase) => ({
        ...phase,
        minutes: minutesFromPhase(phase)
      })));
      setImageFailed(false);
    }
    // Reset when the setup modal opens; keep edits stable during re-renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const totalMinutes = useMemo(
    () => draft.reduce((sum, phase) => sum + Number(phase.minutes || 0), 0),
    [draft]
  );

  const isValid = draft.every((phase) => Number(phase.minutes) >= 1) && totalMinutes <= MAX_MINUTES;

  const updatePhase = (index, value) => {
    const minutes = Math.max(1, Math.min(MAX_MINUTES, Number(value) || 1));
    setDraft((current) => current.map((phase, phaseIndex) => (
      phaseIndex === index ? { ...phase, minutes } : phase
    )));
  };

  const resetDefaults = () => {
    setDraft((defaultPhases.length ? defaultPhases : FALLBACK_PHASES).map((phase) => ({
      ...phase,
      minutes: minutesFromPhase(phase)
    })));
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay patient-setup-overlay">
      <div className="modal time-modal patient-setup-modal">
        <div className="modal-header">
          <div className="modal-title">
            <FiClock size={24} />
            <h2>{t('setExamTimer')}</h2>
          </div>
          <button className="close-modal" onClick={onClose}>
            <FiX size={24} />
          </button>
        </div>

        <div className="patient-setup-body">
          {localizedPatient && (
            <section className="patient-preview-card">
              <div className="patient-preview-image">
                {!imageFailed && patientImage ? (
                  <img
                    src={patientImage}
                    alt={localizedPatient.name}
                    onError={() => setImageFailed(true)}
                  />
                ) : (
                  <span>{(localizedPatient.name || '?')[0]}</span>
                )}
              </div>
              <div className="patient-preview-copy">
                <span className="patient-preview-kicker">{t('patientProfile')}</span>
                <h3>{localizedPatient.name}</h3>
                <div className="patient-preview-meta">
                  {localizedPatient.age && <span>{localizedPatient.age} {t('years')}</span>}
                  {localizedPatient.gender && <span>{localizedPatient.gender}</span>}
                  {localizedPatient.ethnicity && <span>{localizedPatient.ethnicity}</span>}
                </div>
                {localizedPatient.chiefComplaint && (
                  <div className="patient-presenting-problem">
                    <small>{t('presentingComplaint')}</small>
                    <p>{localizedPatient.chiefComplaint}</p>
                  </div>
                )}
              </div>
            </section>
          )}

          <section className="phase-editor">
            <div className="phase-editor-heading">
              <div>
                <h3>{t('sessionStages')}</h3>
                <p>{t('stageHelp')}</p>
              </div>
              <button type="button" onClick={resetDefaults}>
                <FiRefreshCw /> {t('restoreDefault')}
              </button>
            </div>

            <div className="phase-editor-list">
              {draft.map((phase, index) => (
                <label className="phase-editor-row" key={phase.name}>
                  <span>
                    <b>{String(index + 1).padStart(2, '0')}</b>
                    {phaseName(phase.name)}
                  </span>
                  <span className="phase-minute-input">
                    <input
                      type="number"
                      min="1"
                      max={MAX_MINUTES}
                      value={phase.minutes}
                      onChange={(event) => updatePhase(index, event.target.value)}
                    />
                    <em>{t('minutesShort')}</em>
                  </span>
                </label>
              ))}
            </div>

            <div className={`phase-total ${!isValid ? 'invalid' : ''}`}>
              <span>{t('totalSession')}</span>
              <strong>{totalMinutes} / {MAX_MINUTES} {t('minutesShort')}</strong>
            </div>
            {!isValid && <p className="phase-error">{t('reduceStages')}</p>}
          </section>
        </div>

        <div className="patient-setup-actions">
          <button type="button" className="btn-secondary" onClick={onClose}>
            {t('cancel')}
          </button>
          <button
            type="button"
            className="btn-primary start-chatting-btn"
            disabled={!isValid}
            onClick={() => onStartSession(draft)}
          >
            {t('startSession')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TimerModal;
